import { useEffect, useState, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUSES, formatDateID, waLink } from "@/lib/constants";
import { StatusBadge, ServiceBadge, PaymentBadge } from "@/components/Badges";
import DetailDialog from "@/components/DetailDialog";
import { OrderDetailDialog } from "@/pages/Orders";
import RescheduleDialog from "@/components/RescheduleDialog";
import RefundDialog from "@/components/RefundDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  MagnifyingGlass,
  Eye,
  PencilSimple,
  Trash,
  CheckCircle,
  ArrowsClockwise,
  ArrowUUpLeft,
  WhatsappLogo,
  Plus,
  FunnelSimple,
  FileXls,
  FilePdf,
  CaretDown,
} from "@phosphor-icons/react";

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition-colors focus:border-rose-400 focus:ring-2 focus:ring-rose-100";

export default function AllReservations() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ tanggal: "", start: "", end: "", kategori: "", jenis_layanan: "", petugas: "", status: "", uk_min: "", uk_max: "", unit: "" });
  const [meta, setMeta] = useState({ categories: [], services: [], staff: [], historical_services: [] });
  const [showFilters, setShowFilters] = useState(false);
  const [detail, setDetail] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);
  const openOrderDetail = async (oid) => {
    try { const { data } = await api.get(`/orders/${oid}`); setOrderDetail(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const [reschedule, setReschedule] = useState(null);
  const [refundRes, setRefundRes] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [openOrder, setOpenOrder] = useState(null);

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectable = items.filter((r) => !r.is_order);
  const allShownSelected = selectable.length > 0 && selectable.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(() => allShownSelected ? new Set() : new Set(selectable.map((r) => r.id)));
  const selectedList = items.filter((r) => selected.has(r.id));
  const selectedPaidCount = selectedList.filter((r) => r.payment_mode && r.payment_mode !== "none").length;

  const canRefund = (r) => isSuper && r.payment_mode && r.payment_mode !== "none" && r.status !== "Batal";

  const load = useCallback(async () => {
    setLoading(true);
    const params = { group_orders: true };
    if (q) params.q = q;
    if (filters.tanggal) params.tanggal = filters.tanggal;
    if (filters.start) params.start = filters.start;
    if (filters.end) params.end = filters.end;
    if (filters.kategori) params.kategori = filters.kategori;
    if (filters.unit) params.unit = filters.unit;
    if (filters.jenis_layanan) params.jenis_layanan = filters.jenis_layanan;
    if (filters.petugas) params.petugas = filters.petugas;
    if (filters.status) params.status = filters.status;
    if (filters.uk_min) params.usia_kehamilan_min = filters.uk_min;
    if (filters.uk_max) params.usia_kehamilan_max = filters.uk_max;
    try {
      const { data } = await api.get("/reservations", { params });
      setItems(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, filters]);

  useEffect(() => {
    api.get("/reservations/filter-meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  // Jenis Layanan options: services in the selected kategori (or all active services + historical when no kategori).
  const serviceOptions = (() => {
    const active = meta.services.filter((s) => s.aktif !== false);
    if (filters.kategori) {
      return meta.services.filter((s) => s.kategori === filters.kategori).map((s) => s.nama);
    }
    const names = new Set(active.map((s) => s.nama));
    (meta.historical_services || []).forEach((h) => names.add(h));
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  })();

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const confirm = async (r) => {
    try {
      await api.patch(`/reservations/${r.id}/status`, { status: "Dikonfirmasi" });
      toast.success("Reservasi dikonfirmasi");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const doDelete = async () => {
    try {
      await api.post("/reservations/bulk-delete", { ids: [toDelete.id] });
      toast.success("Reservasi dipindahkan ke Sampah");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const doBulkDelete = async () => {
    setBulkBusy(true);
    try {
      const ids = Array.from(selected);
      await api.post("/reservations/bulk-delete", { ids });
      toast.success(`${ids.length} reservasi dipindahkan ke Sampah`);
      setBulkConfirm(false);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBulkBusy(false); }
  };

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v, ...(k === "kategori" ? { jenis_layanan: "" } : {}) }));
  const resetFilters = () => setFilters({ tanggal: "", start: "", end: "", kategori: "", jenis_layanan: "", petugas: "", status: "", uk_min: "", uk_max: "", unit: "" });

  const downloadExport = async (fmt) => {
    const params = { format: fmt };
    if (q) params.q = q;
    if (filters.tanggal) params.tanggal = filters.tanggal;
    if (filters.start) params.start = filters.start;
    if (filters.end) params.end = filters.end;
    if (filters.kategori) params.kategori = filters.kategori;
    if (filters.unit) params.unit = filters.unit;
    if (filters.jenis_layanan) params.jenis_layanan = filters.jenis_layanan;
    if (filters.petugas) params.petugas = filters.petugas;
    if (filters.status) params.status = filters.status;
    try {
      const r = await api.get("/export/reservations", { params, responseType: "blob" });
      const suffix = filters.tanggal || (filters.start && filters.end ? `${filters.start}_sd_${filters.end}` : "semua");
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data);
      a.download = `Reservasi_${suffix}.${fmt === "pdf" ? "pdf" : "xlsx"}`; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
      toast.success("Laporan reservasi berhasil diunduh");
    } catch (e) { toast.error("Gagal mengekspor laporan"); }
  };

  return (
    <div className="animate-fade-up space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-800">Semua Reservasi</h1>
          <p className="mt-1 text-sm text-slate-500">{items.length} reservasi ditemukan</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button data-testid="nav-to-trash" onClick={() => navigate("/sampah")} className="flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-200"><Trash size={16} weight="duotone" /> Sampah</button>
          <button data-testid="reservasi-export-xlsx" onClick={() => downloadExport("xlsx")} className="flex items-center gap-2 rounded-xl bg-emerald-700 px-3 py-2.5 text-sm font-bold text-white hover:bg-emerald-800"><FileXls size={16} weight="duotone" /> Excel</button>
          <button data-testid="reservasi-export-pdf" onClick={() => downloadExport("pdf")} className="flex items-center gap-2 rounded-xl bg-rose-600 px-3 py-2.5 text-sm font-bold text-white hover:bg-rose-700"><FilePdf size={16} weight="duotone" /> PDF</button>
          <button
            data-testid="add-reservation-button"
            onClick={() => navigate("/tambah")}
            className="flex items-center gap-2 rounded-xl bg-[#7C4DE0] px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:bg-[#6A38C7] active:scale-95"
          >
            <Plus size={18} weight="bold" /> Tambah Reservasi
          </button>
        </div>
      </div>

      {/* Search & filter toggle */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari nama atau nomor WhatsApp..."
            className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
          />
        </div>
        <button
          data-testid="toggle-filters"
          onClick={() => setShowFilters((s) => !s)}
          className={`flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors ${
            showFilters ? "border-rose-300 bg-[#EEE6FF] text-[#6A38C7]" : "border-slate-200 bg-white text-slate-600"
          }`}
        >
          <FunnelSimple size={18} /> Filter
        </button>
      </div>

      {showFilters && (
        <div className="grid grid-cols-1 gap-3 rounded-2xl border border-rose-100/70 bg-white p-4 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Tanggal Kunjungan</label>
            <input type="date" data-testid="filter-tanggal" value={filters.tanggal} onChange={(e) => setF("tanggal", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Dari Tanggal</label>
            <input type="date" data-testid="filter-start" value={filters.start} onChange={(e) => setF("start", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Sampai Tanggal</label>
            <input type="date" data-testid="filter-end" value={filters.end} onChange={(e) => setF("end", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Kategori Layanan</label>
            <select data-testid="filter-kategori" value={filters.kategori} onChange={(e) => setF("kategori", e.target.value)} className={inputCls}>
              <option value="">Semua Kategori</option>
              {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Poli / Unit</label>
            <select data-testid="filter-unit" value={filters.unit} onChange={(e) => setF("unit", e.target.value)} className={inputCls}>
              <option value="">Semua Unit</option>
              {(meta.units || []).map((u) => <option key={u.id} value={u.id}>{u.nama}</option>)}
              <option value="unmapped">Belum Dipetakan</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Jenis Layanan</label>
            <select data-testid="filter-layanan" value={filters.jenis_layanan} onChange={(e) => setF("jenis_layanan", e.target.value)} className={inputCls}>
              <option value="">Semua Layanan</option>
              {serviceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Petugas</label>
            <select data-testid="filter-petugas" value={filters.petugas} onChange={(e) => setF("petugas", e.target.value)} className={inputCls}>
              <option value="">Semua Petugas</option>
              {meta.staff.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Status</label>
            <select data-testid="filter-status" value={filters.status} onChange={(e) => setF("status", e.target.value)} className={inputCls}>
              <option value="">Semua Status</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-semibold text-slate-500">UK min (mgg)</label>
              <input type="number" data-testid="filter-uk-min" value={filters.uk_min} onChange={(e) => setF("uk_min", e.target.value)} className={inputCls} />
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-xs font-semibold text-slate-500">UK max (mgg)</label>
              <input type="number" data-testid="filter-uk-max" value={filters.uk_max} onChange={(e) => setF("uk_max", e.target.value)} className={inputCls} />
            </div>
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <button onClick={resetFilters} className="text-sm font-semibold text-rose-500 hover:underline">Reset Filter</button>
          </div>
        </div>
      )}

      {selected.size > 0 && (
        <div data-testid="bulk-action-bar" className="sticky top-2 z-10 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#7C4DE0]/30 bg-[#F3ECFF] px-4 py-3 shadow-sm">
          <span className="text-sm font-bold text-[#6A38C7]" data-testid="bulk-count">{selected.size} reservasi dipilih</span>
          <div className="flex items-center gap-2">
            <button data-testid="bulk-clear" onClick={() => setSelected(new Set())} className="rounded-xl px-3 py-2 text-sm font-semibold text-slate-500 hover:bg-white">Batal Pilih</button>
            <button data-testid="bulk-delete-btn" onClick={() => setBulkConfirm(true)} className="inline-flex items-center gap-1.5 rounded-xl bg-rose-600 px-4 py-2 text-sm font-bold text-white hover:bg-rose-700"><Trash size={16} weight="bold" /> Pindahkan ke Sampah</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-rose-200 border-t-rose-500" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-rose-200 bg-white/50 p-12 text-center text-sm text-slate-500">
          Tidak ada reservasi yang cocok.
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-x-auto rounded-2xl border border-rose-100/70 bg-white lg:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-rose-100 bg-[#F7F0FF] text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-3"><input data-testid="select-all" type="checkbox" checked={allShownSelected} onChange={toggleAll} className="h-4 w-4 cursor-pointer accent-[#7C4DE0]" /></th>
                  <th className="px-3 py-3">No</th>
                  <th className="px-3 py-3">Tgl / Jam</th>
                  <th className="px-3 py-3">Nama</th>
                  <th className="px-3 py-3">Usia</th>
                  <th className="px-3 py-3">Hamil ke-</th>
                  <th className="px-3 py-3">UK</th>
                  <th className="px-3 py-3">Layanan</th>
                  <th className="px-3 py-3">Keluhan</th>
                  <th className="px-3 py-3">WhatsApp</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, i) => r.is_order ? (
                  <Fragment key={r.id}>
                    <tr data-testid={`order-row-${r.order_id}`} className="border-b border-rose-50 bg-[#FAF7FF]/70">
                      <td className="px-3 py-3"></td>
                      <td className="px-3 py-3 text-slate-400">{i + 1}</td>
                      <td className="px-3 py-3">
                        <div className="font-semibold text-slate-700">{formatDateID(r.tanggal_kunjungan)}</div>
                        <div className="text-xs text-slate-400">{r.jam_kunjungan}</div>
                      </td>
                      <td className="px-3 py-3 font-semibold text-slate-800">{r.nama_lengkap} <span className="ml-1 rounded bg-[#7C4DE0] px-1.5 py-0.5 text-[10px] font-bold text-white">Multi · {r.children_count}</span></td>
                      <td className="px-3 py-3 text-slate-400">-</td>
                      <td className="px-3 py-3 text-slate-400">-</td>
                      <td className="px-3 py-3 text-slate-400">-</td>
                      <td className="px-3 py-3"><span className="text-slate-600">{r.jenis_layanan}</span><span className="ml-1 rounded bg-[#EEE6FF] px-1.5 py-0.5 text-[10px] font-bold text-[#6A38C7]" data-testid="order-badge">{r.order_no}</span></td>
                      <td className="px-3 py-3 text-slate-400">-</td>
                      <td className="px-3 py-3 text-slate-600">{r.no_whatsapp}</td>
                      <td className="px-3 py-3"><div className="flex flex-col items-start gap-1"><StatusBadge status={r.status} /><PaymentBadge status={r.payment_status} /></div></td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1">
                          <IconBtn testid={`wa-${r.order_id}`} title="WhatsApp" href={waLink(r.no_whatsapp, `Halo ${r.nama_lengkap}, terkait pesanan ${r.order_no} Anda di GBS Clinic.`)} className="text-[#25D366]"><WhatsappLogo size={18} weight="fill" /></IconBtn>
                          <IconBtn testid={`order-detail-${r.order_id}`} title="Detail pesanan" onClick={() => openOrderDetail(r.order_id)} className="text-sky-600"><Eye size={18} /></IconBtn>
                          <IconBtn testid={`order-expand-${r.order_id}`} title="Lihat layanan" onClick={() => setOpenOrder(openOrder === r.order_id ? null : r.order_id)} className="text-[#6A38C7]"><CaretDown size={18} className={`transition-transform ${openOrder === r.order_id ? "rotate-180" : ""}`} /></IconBtn>
                        </div>
                      </td>
                    </tr>
                    {openOrder === r.order_id && r.children.map((c) => (
                      <tr key={c.id} className="border-b border-rose-50 bg-white">
                        <td className="px-3 py-2"></td><td className="px-3 py-2"></td>
                        <td className="px-3 py-2 text-xs text-slate-400" colSpan={2}>↳ {c.jam_kunjungan || "-"} · <span className="font-semibold text-slate-600">{c.nama_lengkap}</span></td>
                        <td className="px-3 py-2" colSpan={3}></td>
                        <td className="px-3 py-2"><ServiceBadge service={c.jenis_layanan} /></td>
                        <td className="px-3 py-2 text-xs text-slate-500">{c.petugas_nama || "-"}</td>
                        <td className="px-3 py-2"></td>
                        <td className="px-3 py-2"><PaymentBadge status={r.payment_status} /></td>
                        <td className="px-3 py-2"></td>
                      </tr>
                    ))}
                  </Fragment>
                ) : (
                  <tr key={r.id} data-testid={`row-${r.id}`} className={`border-b border-rose-50 last:border-0 hover:bg-rose-50/40 ${selected.has(r.id) ? "bg-[#F3ECFF]" : ""}`}>
                    <td className="px-3 py-3"><input data-testid={`select-${r.id}`} type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSel(r.id)} className="h-4 w-4 cursor-pointer accent-[#7C4DE0]" /></td>
                    <td className="px-3 py-3 text-slate-400">{i + 1}</td>
                    <td className="px-3 py-3">
                      <div className="font-semibold text-slate-700">{formatDateID(r.tanggal_kunjungan)}</div>
                      <div className="text-xs text-slate-400">{r.jam_kunjungan}</div>
                    </td>
                    <td className="px-3 py-3 font-semibold text-slate-800">{r.nama_lengkap}</td>
                    <td className="px-3 py-3 text-slate-600">{r.usia || "-"}</td>
                    <td className="px-3 py-3 text-slate-600">{r.kehamilan_ke ?? "-"}</td>
                    <td className="px-3 py-3 font-bold text-[#6A38C7]">{r.usia_kehamilan_minggu ? `${r.usia_kehamilan_minggu} mgg` : "-"}</td>
                    <td className="px-3 py-3"><ServiceBadge service={r.jenis_layanan} />{r.order_no && <span className="ml-1 rounded bg-[#EEE6FF] px-1.5 py-0.5 text-[10px] font-bold text-[#6A38C7]" data-testid="order-badge">{r.order_no}</span>}</td>
                    <td className="px-3 py-3 max-w-[180px] truncate text-slate-600" title={r.keluhan}>{r.keluhan || "-"}</td>
                    <td className="px-3 py-3 text-slate-600">{r.no_whatsapp}</td>
                    <td className="px-3 py-3"><div className="flex flex-col items-start gap-1"><StatusBadge status={r.status} /><PaymentBadge status={r.payment_status} /></div></td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-1">
                        <IconBtn testid={`wa-${r.id}`} title="WhatsApp" href={waLink(r.no_whatsapp, `Halo ${r.nama_lengkap}, terkait reservasi Anda di GBS Clinic.`)} className="text-[#25D366]"><WhatsappLogo size={18} weight="fill" /></IconBtn>
                        <IconBtn testid={`detail-${r.id}`} title="Detail" onClick={() => setDetail(r)} className="text-sky-600"><Eye size={18} /></IconBtn>
                        <IconBtn testid={`edit-${r.id}`} title="Edit" onClick={() => navigate(`/reservasi/${r.id}/edit`)} className="text-amber-600"><PencilSimple size={18} /></IconBtn>
                        <IconBtn testid={`confirm-${r.id}`} title="Konfirmasi" onClick={() => confirm(r)} className="text-emerald-600"><CheckCircle size={18} /></IconBtn>
                        <IconBtn testid={`reschedule-${r.id}`} title="Reschedule" onClick={() => setReschedule(r)} className="text-orange-600"><ArrowsClockwise size={18} /></IconBtn>
                        {canRefund(r) && <IconBtn testid={`refund-${r.id}`} title="Batalkan & Refund" onClick={() => setRefundRes(r)} className="text-rose-500"><ArrowUUpLeft size={18} /></IconBtn>}
                        <IconBtn testid={`delete-${r.id}`} title="Hapus" onClick={() => setToDelete(r)} className="text-rose-600"><Trash size={18} /></IconBtn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-3 lg:hidden">
            {items.map((r) => r.is_order ? (
              <div key={r.id} data-testid={`order-card-${r.order_id}`} className="rounded-2xl border border-[#7C4DE0]/30 bg-[#FAF7FF] p-4 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-heading font-bold text-slate-800">{r.nama_lengkap} <span className="ml-1 rounded bg-[#7C4DE0] px-1.5 py-0.5 text-[10px] font-bold text-white">Multi · {r.children_count}</span></p>
                    <p className="text-xs text-slate-400">{formatDateID(r.tanggal_kunjungan)} · {r.jam_kunjungan}</p>
                    <p className="text-[11px] font-bold text-[#6A38C7]" data-testid="order-badge">{r.order_no}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1"><StatusBadge status={r.status} /><PaymentBadge status={r.payment_status} /></div>
                </div>
                <button data-testid={`m-order-detail-${r.order_id}`} onClick={() => openOrderDetail(r.order_id)} className="mr-3 mt-2 inline-flex items-center gap-1 text-xs font-bold text-sky-600"><Eye size={14} /> Detail</button>
                <button data-testid={`m-order-expand-${r.order_id}`} onClick={() => setOpenOrder(openOrder === r.order_id ? null : r.order_id)} className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#6A38C7]"><CaretDown size={14} className={`transition-transform ${openOrder === r.order_id ? "rotate-180" : ""}`} /> {r.jenis_layanan}</button>
                {openOrder === r.order_id && (
                  <ul className="mt-2 space-y-1">
                    {r.children.map((c) => (
                      <li key={c.id} className="rounded-lg bg-white px-2.5 py-1.5 text-xs text-slate-600">{c.jenis_layanan} · {c.jam_kunjungan || "-"} · {c.petugas_nama || "Tanpa petugas"}</li>
                    ))}
                  </ul>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-rose-50 pt-3">
                  <a href={waLink(r.no_whatsapp, `Halo ${r.nama_lengkap}, terkait pesanan ${r.order_no} Anda di GBS Clinic.`)} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded-lg bg-[#25D366] px-3 py-1.5 text-xs font-bold text-white"><WhatsappLogo size={16} weight="fill" /> WA</a>
                </div>
              </div>
            ) : (
              <div key={r.id} data-testid={`card-${r.id}`} className={`rounded-2xl border bg-white p-4 shadow-sm ${selected.has(r.id) ? "border-[#7C4DE0]" : "border-rose-100/70"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-start gap-2">
                    <input data-testid={`m-select-${r.id}`} type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSel(r.id)} className="mt-1 h-4 w-4 cursor-pointer accent-[#7C4DE0]" />
                    <div className="min-w-0">
                      <p className="font-heading font-bold text-slate-800">{r.nama_lengkap}</p>
                      <p className="text-xs text-slate-400">{formatDateID(r.tanggal_kunjungan)} · {r.jam_kunjungan}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1"><StatusBadge status={r.status} /><PaymentBadge status={r.payment_status} /></div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <ServiceBadge service={r.jenis_layanan} />{r.order_no && <span className="ml-1 rounded bg-[#EEE6FF] px-1.5 py-0.5 text-[10px] font-bold text-[#6A38C7]" data-testid="order-badge">{r.order_no}</span>}
                  {r.usia_kehamilan_minggu ? (
                    <span className="rounded-full bg-rose-50 px-2.5 py-0.5 text-xs font-bold text-[#6A38C7]">UK {r.usia_kehamilan_minggu} mgg</span>
                  ) : null}
                  {r.kehamilan_ke ? <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">Hamil ke-{r.kehamilan_ke}</span> : null}
                </div>
                {r.keluhan ? <p className="mt-2 text-sm text-slate-600"><span className="font-semibold">Keluhan:</span> {r.keluhan}</p> : null}
                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-rose-50 pt-3">
                  <a href={waLink(r.no_whatsapp, `Halo ${r.nama_lengkap}, terkait reservasi Anda di GBS Clinic.`)} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded-lg bg-[#25D366] px-3 py-1.5 text-xs font-bold text-white"><WhatsappLogo size={16} weight="fill" /> WA</a>
                  <MBtn testid={`m-detail-${r.id}`} onClick={() => setDetail(r)}>Detail</MBtn>
                  <MBtn testid={`m-edit-${r.id}`} onClick={() => navigate(`/reservasi/${r.id}/edit`)}>Edit</MBtn>
                  <MBtn testid={`m-confirm-${r.id}`} onClick={() => confirm(r)}>Konfirmasi</MBtn>
                  <MBtn testid={`m-reschedule-${r.id}`} onClick={() => setReschedule(r)}>Reschedule</MBtn>
                  {canRefund(r) && <MBtn testid={`m-refund-${r.id}`} onClick={() => setRefundRes(r)}>Refund</MBtn>}
                  <MBtn testid={`m-delete-${r.id}`} onClick={() => setToDelete(r)} danger>Hapus</MBtn>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <DetailDialog reservation={detail} open={!!detail} onClose={() => setDetail(null)} onChanged={load} />
      {orderDetail && <OrderDetailDialog order={orderDetail} onClose={() => setOrderDetail(null)} onChange={(o) => { setOrderDetail(o || null); load(); }} />}
      <RescheduleDialog
        reservation={reschedule}
        open={!!reschedule}
        onClose={() => setReschedule(null)}
        onDone={() => { setReschedule(null); load(); }}
      />
      <RefundDialog
        reservation={refundRes}
        open={!!refundRes}
        onClose={() => setRefundRes(null)}
        onDone={() => { setRefundRes(null); load(); }}
      />

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Pindahkan ke Sampah?</AlertDialogTitle>
            <AlertDialogDescription>
              Reservasi milik <b>{toDelete?.nama_lengkap}</b> akan dipindahkan ke Sampah dan tidak lagi muncul di daftar aktif. Anda dapat memulihkannya nanti.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete" onClick={doDelete} className="bg-rose-600 hover:bg-rose-700">Pindahkan ke Sampah</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkConfirm} onOpenChange={(v) => !v && setBulkConfirm(false)}>
        <AlertDialogContent className="rounded-2xl" data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus {selected.size} reservasi yang dipilih?</AlertDialogTitle>
            <AlertDialogDescription>
              Reservasi akan dipindahkan ke Sampah dan tidak lagi muncul di daftar aktif.
              {selectedPaidCount > 0 && (
                <span className="mt-2 block rounded-lg bg-amber-50 px-3 py-2 font-semibold text-amber-700">⚠️ {selectedPaidCount} di antaranya memiliki data pembayaran. Data pembayaran & riwayat tetap disimpan; reservasi berbayar tidak dapat dihapus permanen.</span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-bulk-delete" disabled={bulkBusy} onClick={doBulkDelete} className="bg-rose-600 hover:bg-rose-700">Pindahkan ke Sampah</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function IconBtn({ children, onClick, href, title, className, testid }) {
  const cls = `flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-slate-100 ${className}`;
  if (href)
    return <a href={href} target="_blank" rel="noreferrer" title={title} data-testid={testid} className={cls}>{children}</a>;
  return <button title={title} onClick={onClick} data-testid={testid} className={cls}>{children}</button>;
}

function MBtn({ children, onClick, danger, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${danger ? "bg-rose-50 text-rose-600" : "bg-slate-100 text-slate-600"}`}
    >
      {children}
    </button>
  );
}
